import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';
import { appwriteQueryFns } from '../services/query/appwriteQueryFns';
import { useAuth } from '../contexts/AuthContext';
import type { TeamPreferences } from '../services/teamPreferences.service';

/**
 * Fetches team preferences. These are NOT optional — they gate which modules
 * and options are visible in the UI. The query starts as soon as the component
 * tree mounts and stays in cache for the entire session (gcTime: 24h).
 *
 * Data is also prefetched at app boot by usePrefetchPreferences, but this hook
 * does NOT rely on that — it fires independently and caches aggressively.
 */
export const useTeamPreferences = () => {
  const { currentUser, targetEmail } = useAuth();
  const userId = currentUser?.uid;
  const impersonatedEmail = targetEmail && targetEmail !== currentUser?.email ? targetEmail : null;

  return useQuery({
    queryKey: queryKeys.preferences.team(impersonatedEmail || userId || ''),
    queryFn: () => {
      if (!userId) {
        // No user identity yet — return null so consumers check for it.
        return null as unknown as TeamPreferences;
      }
      return appwriteQueryFns.preferences.team();
    },
    // ALWAYS enabled — even when userId is null, the queryFn handles it gracefully.
    // This means the query starts on component mount, not after auth resolves.
    // Once userId becomes available, the query key changes and a real fetch fires.
    enabled: true,
    // 5 min — mounted in AppLayout/Sidebar (every page); a 30s window re-fired
    // the teams.list + teams prefs pair on every navigation.
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000, // 24 hours — never evict during a session
    retry: 1,
    placeholderData: (previousData: any) => previousData,
  });
};